import { useEffect, useMemo, useRef } from 'react'
import { Platform } from 'react-native'

import type { B2bAccountUserData } from '../../accounts/types/b2bAccount.types'
import type { ConsumerProBillingInterval } from '../constants/consumerProBilling.constants'
import {
  clearPersistedConsumerPricingSnapshot,
  readPersistedConsumerPricingSnapshot,
  writePersistedConsumerPricingSnapshot,
} from '../helpers/pricingStableSessionSnapshot.helper'

export interface StableConsumerPlanForPricing {
  consumerPlanTier: 'FREE' | 'PRO'
  consumerProBillingInterval: ConsumerProBillingInterval | null
  consumerCancelAtPeriodEnd: boolean
  consumerPeriodEndIso: string | null
}

type CommittedSnapshot = StableConsumerPlanForPricing

function snapshotFromUser(user: B2bAccountUserData): CommittedSnapshot {
  const consumerBilling = user.consumerBilling
  if (!consumerBilling) {
    return FREE_SNAPSHOT
  }

  return {
    consumerPlanTier: consumerBilling.planTier,
    consumerProBillingInterval: consumerBilling.proBillingInterval ?? null,
    consumerCancelAtPeriodEnd: consumerBilling.cancelAtPeriodEnd === true,
    consumerPeriodEndIso: consumerBilling.stripeCurrentPeriodEnd ?? null,
  }
}

const FREE_SNAPSHOT: CommittedSnapshot = {
  consumerPlanTier: 'FREE',
  consumerProBillingInterval: null,
  consumerCancelAtPeriodEnd: false,
  consumerPeriodEndIso: null,
}

/**
 * While account admin is refetching (e.g. Stripe sync + GET /accounts/user in parallel),
 * avoid regressing pricing UI from Pro → Free until the latest load settles — prevents
 * plan cards and CTAs from flashing Hobby between overlapping requests.
 *
 * On web, persists the last settled Pro snapshot to `sessionStorage` so an SPA navigation
 * back to `/packs` (no full document reload) still has a tier hint until `user` is hydrated.
 */
export function useStableConsumerPlanForPricing(
  user: B2bAccountUserData | null,
  accountAdminLoading: boolean,
): StableConsumerPlanForPricing {
  const committedRef = useRef<CommittedSnapshot | null>(null)

  useEffect(() => {
    if (!user) {
      if (!accountAdminLoading) {
        committedRef.current = null
        clearPersistedConsumerPricingSnapshot()
      }
      return
    }
    if (accountAdminLoading) {
      return
    }
    const snap = snapshotFromUser(user)
    committedRef.current = snap
    if (Platform.OS === 'web' && snap.consumerPlanTier === 'PRO') {
      writePersistedConsumerPricingSnapshot(snap)
    }
    if (Platform.OS === 'web' && snap.consumerPlanTier === 'FREE') {
      clearPersistedConsumerPricingSnapshot()
    }
  }, [accountAdminLoading, user])

  return useMemo(() => {
    if (!user) {
      if (accountAdminLoading) {
        const persisted = readPersistedConsumerPricingSnapshot()
        if (persisted?.consumerPlanTier === 'PRO') {
          return persisted
        }
      }
      if (accountAdminLoading && committedRef.current?.consumerPlanTier === 'PRO') {
        return committedRef.current
      }
      return FREE_SNAPSHOT
    }
    const live = snapshotFromUser(user)
    if (accountAdminLoading && committedRef.current?.consumerPlanTier === 'PRO') {
      return committedRef.current
    }
    return live
  }, [user, accountAdminLoading])
}
